import { useMemo, useState } from 'react';
import { AddGuestToFunctions, UpdateGuest, SetGuestFunctions } from '../../api/api';
import { isPastFunction, normaliseText, COUNT_GROUP_TYPE, NO_CARD_STATUS } from '../../utils/helpers';

// "Add group (count only)" - guests known only by number: Lavina's School
// Friends, 6 people, Thread Ceremony only. ONE Evt_Guests row: GuestName = the
// group's name, ExpectedCount = persons (the headcount already counts a party
// by its ExpectedCount), GuestType 'Group', and the member whose guests they
// are as the adder (CreatedBy / AddedByName), so "Added by" finds them.
// A card is given or not: not = CardStatus 'None' (Cards / Labels skip it).
// Used by useMembers, which hands in what it already knows.
const blank = () => ({ GuestId: null, GuestName: '', Persons: '', MemberId: '', FunctionIds: [], Card: false });
// Whose guests + card, remembered per event: groups are usually entered in a
// row for one person (Lavina's School / Gym / College friends).
const lastKey = (eventId) => `se_group_last:${eventId}`;
const readLast = (eventId) => { try { return JSON.parse(localStorage.getItem(lastKey(eventId))) || {}; } catch { return {}; } };
const writeLast = (eventId, v) => { try { localStorage.setItem(lastKey(eventId), JSON.stringify(v)); } catch { /* private mode */ } };

export function useCountGroup({ event, organiser, user, functions, team, isMarriage, refresh, toast, rowFunctionIds }) {
  const me = user.UserId;
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState(blank);
  const [groupSaving, setGroupSaving] = useState(false);

  // Whose guests: the event's members (vendors are not inviting anyone).
  const memberOptions = useMemo(() => (team || [])
    .filter(t => String(t.EventRole || '') !== 'Vendor')
    .map(t => ({
      value: String(t.UserId),
      label: [t.Surname, t.Name].filter(Boolean).join(' ') || t.FullName || `User ${t.UserId}`,
      mobile: t.MobileNumber || null,
      side: t.Side || 'Common',
    }))
    .sort((a, b) => a.label.localeCompare(b.label)), [team]);

  // Open functions only - and, when editing, the ones it already has.
  const groupFunctions = useMemo(() => functions.filter(f => !isPastFunction(f)
    || groupForm.FunctionIds.includes(String(f.FunctionId))), [functions, groupForm.FunctionIds]);

  const setG = (k, v) => setGroupForm(f => ({ ...f, [k]: v }));
  const toggleGroupFunction = (id) => setGroupForm(f => {
    const s = String(id);
    return { ...f, FunctionIds: f.FunctionIds.includes(s) ? f.FunctionIds.filter(x => x !== s) : [...f.FunctionIds, s] };
  });

  const openGroupAdd = () => {
    const last = readLast(event.EventId);
    const known = memberOptions.some(m => m.value === String(last.MemberId || ''));
    setGroupForm({ ...blank(), MemberId: known ? String(last.MemberId) : '', Card: !!last.Card });
    setGroupOpen(true);
  };
  const setAllGroupFunctions = (on) => setGroupForm(f => ({
    ...f, FunctionIds: on ? groupFunctions.filter(fn => !isPastFunction(fn)).map(fn => String(fn.FunctionId)) : [],
  }));
  const openGroupEdit = (row) => {
    setGroupForm({
      GuestId: row.GuestId,
      GuestName: row.GuestName || '',
      Persons: String(row.ExpectedCount || 1),
      MemberId: String(row.CreatedBy || ''),
      FunctionIds: rowFunctionIds(row).map(String),
      Card: String(row.CardStatus || '') !== NO_CARD_STATUS,
    });
    setGroupOpen(true);
  };

  const groupProblem = () => {
    if (!String(groupForm.GuestName).trim()) return 'Give the group a name, e.g. School Friends';
    const n = Number(groupForm.Persons);
    if (!Number.isInteger(n) || n < 1 || n > 500) return 'Persons must be a number from 1 to 500';
    if (!groupForm.MemberId) return 'Choose whose guests they are';
    if (!groupForm.FunctionIds.length) return 'Tick at least one function';
    return '';
  };

  // next = "Save & next": keep whose / functions / card, clear name + persons.
  const saveGroup = async ({ next = false } = {}) => {
    const problem = groupProblem();
    if (problem) { toast(problem); return; }
    const member = memberOptions.find(m => m.value === String(groupForm.MemberId));
    const name = normaliseText(String(groupForm.GuestName).trim());
    const persons = Number(groupForm.Persons);
    const owner = {
      CreatedBy: Number(groupForm.MemberId),
      AddedByName: member ? member.label : null,
      AddedByMobile: member ? member.mobile : null,
    };
    const extra = {
      GuestName: name, Surname: null, ExpectedCount: persons, GuestType: COUNT_GROUP_TYPE,
      CardStatus: groupForm.Card ? 'Pending' : NO_CARD_STATUS, ...owner, UpdatedBy: me,
    };
    setGroupSaving(true);
    try {
      let id = groupForm.GuestId;
      if (!id) {
        const res = await AddGuestToFunctions({
          EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: groupForm.FunctionIds,
          GuestName: name, Surname: null, MobileNumber: null,
          Side: isMarriage ? (member?.side || 'Common') : 'Common', Visibility: 'Private',
          InviteStatus: 'Pending', IsFamilyHead: 1, FamilyMembers: [], Valid: 1, ...owner, UpdatedBy: me,
        });
        id = res?.insertId || (res?.insertedIds || [])[0];
        if (!id) throw new Error('no id');
      } else {
        await SetGuestFunctions({
          GuestId: id, EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: groupForm.FunctionIds, UpdatedBy: me,
        });
      }
      // The add call always saves 1 person - the count goes on afterwards.
      await UpdateGuest({ data: extra, where: `GuestId = ${Number(id)} and EventId = ${Number(event.EventId)}` });
      toast(`${name} - ${persons} ${persons === 1 ? 'person' : 'persons'} saved`);
      writeLast(event.EventId, { MemberId: groupForm.MemberId, Card: groupForm.Card });
      if (next) setGroupForm(f => ({ ...blank(), MemberId: f.MemberId, FunctionIds: f.FunctionIds, Card: f.Card }));
      else setGroupOpen(false);
      refresh(true);
    } catch {
      toast('Could not save the group - check your connection');
    } finally {
      setGroupSaving(false);
    }
  };

  return {
    groupOpen, setGroupOpen, groupForm, setG, toggleGroupFunction, setAllGroupFunctions, groupFunctions, memberOptions,
    openGroupAdd, openGroupEdit, saveGroup, groupSaving, groupProblem,
  };
}
